import React, { useEffect, useState, useCallback } from "react";
import {
  Storefront, Trash, UploadSimple, ImageSquare, ArrowUp, ArrowDown,
  DotsSixVertical, Eye, EyeSlash, Star,
} from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

/**
 * Admin panel · Unified Product Gallery.
 *
 * Every image (Printful mockup + admin upload) shows in ONE grid.
 * The admin can:
 *   • drag-drop reorder (mix uploads and mockups freely)
 *   • use ↑ / ↓ arrows for keyboard-free reorder
 *   • star an image → makes it the primary/first
 *   • toggle the eye → hides an image from the public shop
 *   • delete admin uploads (Printful mockups can only be hidden)
 * The order + primary + hidden set is persisted server-side via
 *   PUT /api/admin/product-settings/{product_id}
 * and shop.py honors it when serving GET /api/shop/products/{product_id}.
 */
export default function ProductImagesPanel({ token, backend }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [customImages, setCustomImages] = useState([]); // {id, url, original_filename}
  const [printfulImages, setPrintfulImages] = useState([]); // string[] URLs
  const [galleryOrder, setGalleryOrder] = useState([]); // string[] URLs — the unified order
  const [hiddenSet, setHiddenSet] = useState(new Set());
  const [primaryUrl, setPrimaryUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dragUrl, setDragUrl] = useState(null);

  useEffect(() => {
    fetch(`${backend}/api/shop/products?include_kms=1`)
      .then((r) => r.json())
      .then((d) => setProducts(d?.products || []))
      .catch((e) => console.warn("ProductImagesPanel: products fetch failed:", e));
  }, [backend]);

  const loadForProduct = useCallback(async (pid) => {
    if (!pid) {
      setCustomImages([]); setPrintfulImages([]); setHiddenSet(new Set());
      setGalleryOrder([]); setPrimaryUrl("");
      return;
    }
    setLoading(true); setErr(""); setMsg("");
    try {
      // Fetch admin data + Printful detail in parallel. The admin data is CRITICAL
      // (we can still render uploads + reorder saved gallery). The Printful detail
      // is best-effort — if it fails (rate limit, network blip), we degrade gracefully
      // instead of nuking the panel with a fatal error.
      const [adminR, detailR] = await Promise.all([
        fetch(`${backend}/api/admin/product-images/${pid}`, { headers: { "X-Admin-Token": token } }),
        fetch(`${backend}/api/shop/products/${pid}`).catch((e) => ({ ok: false, status: 0, _networkError: e })),
      ]);
      if (!adminR.ok) throw new Error(`admin HTTP ${adminR.status}`);
      const adminD = await adminR.json();
      const uploads = adminD.images || [];
      setCustomImages(uploads);
      const savedHidden = new Set(adminD.hidden_urls || adminD.hidden_printful_urls || []);
      setHiddenSet(savedHidden);
      setPrimaryUrl(adminD.primary_url || "");

      let printful = [];
      let detailFailed = null;
      if (!detailR.ok) {
        const raw = detailR._networkError ? "network error" : `HTTP ${detailR.status}`;
        let msg = raw;
        try {
          const j = detailR.json ? await detailR.json() : {};
          if (j?.detail) msg = j.detail;
        } catch { /* ignore parse */ }
        detailFailed = `Printful mockups unavailable (${msg}). Showing admin uploads + saved gallery only.`;
      } else {
        const detailD = await detailR.json();
        const customUrlSet = new Set(uploads.map((c) => `/api/product-images/${c.id}`));
        const publicVisible = (detailD.images || []).filter((u) => !customUrlSet.has(u));
        const hiddenPrintfulOnly = Array.from(savedHidden).filter((u) => !customUrlSet.has(u));
        printful = Array.from(new Set([...publicVisible, ...hiddenPrintfulOnly]));
      }
      setPrintfulImages(printful);

      // Unified order: use saved gallery_order when set, else compute default
      const saved = adminD.gallery_order || [];
      const allUrls = [...uploads.map((c) => `/api/product-images/${c.id}`), ...printful];
      const orderedFromSaved = saved.filter((u) => allUrls.includes(u));
      const stillMissing = allUrls.filter((u) => !orderedFromSaved.includes(u));
      setGalleryOrder([...orderedFromSaved, ...stillMissing]);

      if (detailFailed) setErr(detailFailed);
    } catch (e) {
      setErr(e.message || "Could not load");
      setCustomImages([]); setPrintfulImages([]); setHiddenSet(new Set());
      setGalleryOrder([]); setPrimaryUrl("");
    } finally {
      setLoading(false);
    }
  }, [backend, token]);

  useEffect(() => { loadForProduct(productId); }, [productId, loadForProduct]);

  const persistSettings = async (patch) => {
    try {
      const r = await fetch(`${backend}/api/admin/product-settings/${productId}`, {
        method: "PUT",
        headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg("Gallery saved ✓");
      setTimeout(() => setMsg(""), 2200);
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!productId) { setErr("Pick a product first"); return; }
    const fileInput = e.currentTarget.querySelector('input[type="file"]');
    const files = Array.from(fileInput?.files || []);
    if (files.length === 0) { setErr("Pick at least one image file"); return; }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("product_id", productId);
        fd.append("file", f);
        const r = await fetch(`${backend}/api/admin/product-images`, {
          method: "POST", headers: { "X-Admin-Token": token }, body: fd,
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${r.status}`);
        }
      }
      setMsg(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
      fileInput.value = "";
      await loadForProduct(productId);
    } catch (e2) {
      setErr(e2.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const move = (url, direction) => {
    const idx = galleryOrder.indexOf(url);
    if (idx < 0) return;
    const swap = idx + direction;
    if (swap < 0 || swap >= galleryOrder.length) return;
    const next = galleryOrder.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setGalleryOrder(next);
    persistSettings({ gallery_order: next });
  };

  const onDragStart = (e, url) => { setDragUrl(url); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e, targetUrl) => {
    e.preventDefault();
    if (!dragUrl || dragUrl === targetUrl) { setDragUrl(null); return; }
    const from = galleryOrder.indexOf(dragUrl);
    const to = galleryOrder.indexOf(targetUrl);
    if (from < 0 || to < 0) { setDragUrl(null); return; }
    const next = galleryOrder.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setGalleryOrder(next);
    persistSettings({ gallery_order: next });
    setDragUrl(null);
  };

  const setPrimary = (url) => {
    setPrimaryUrl(url);
    // Also move it to index 0 in the order for a WYSIWYG effect
    const next = [url, ...galleryOrder.filter((u) => u !== url)];
    setGalleryOrder(next);
    persistSettings({ primary_url: url, gallery_order: next });
  };

  const toggleHidden = (url) => {
    const next = new Set(hiddenSet);
    if (next.has(url)) next.delete(url); else next.add(url);
    setHiddenSet(next);
    persistSettings({ hidden_urls: Array.from(next) });
  };

  const onDelete = async (imgUrl) => {
    // Only admin uploads can be deleted; Printful mockups can be hidden but not removed.
    const custom = customImages.find((c) => `/api/product-images/${c.id}` === imgUrl);
    if (!custom) { setErr("Printful mockups can only be hidden, not deleted."); return; }
    if (!window.confirm("Delete this uploaded image? It will disappear from the shop immediately.")) return;
    try {
      const r = await fetch(`${backend}/api/admin/product-images/${custom.id}`, {
        method: "DELETE", headers: { "X-Admin-Token": token },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg("Image deleted");
      // Remove from local order + persist
      const next = galleryOrder.filter((u) => u !== imgUrl);
      setGalleryOrder(next);
      persistSettings({ gallery_order: next });
      await loadForProduct(productId);
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  };

  const resolveImgSrc = (url) => (url.startsWith("/api/") ? `${backend}${url}` : url);
  const isUpload = (url) => url.startsWith("/api/product-images/");

  return (
    <CollapsibleSection
      id="product-images"
      testId="admin-product-images-panel"
      title={<><Storefront size={20} weight="bold" className="text-[#FF1B91] inline mr-2" />Product gallery</>}
      subtitle="Drag or arrow-reorder · star primary · eye hides · trash removes uploads."
      onRefresh={() => (productId ? loadForProduct(productId) : Promise.resolve())}
    >
      <div className="space-y-5">
      <p className="text-white/60 text-sm leading-relaxed">
        One unified grid. Drag or use the arrows to reorder. Star to pick your primary
        image, eye to hide anything from the shop, trash to remove an upload.
        Printful mockups can be hidden but not deleted.
      </p>

      <label className="block space-y-2">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Pick a product</span>
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          data-testid="admin-product-picker"
          className="w-full bg-black border-2 border-white/20 text-white px-3 py-2.5 text-sm"
        >
          <option value="">— Select —</option>
          {products.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {`${p.kms_exclusive ? "[KMS] " : ""}${p.name} · #${p.id}`}
            </option>
          ))}
        </select>
      </label>

      {productId && (
        <form onSubmit={onUpload} className="space-y-2 border-t border-white/10 pt-4">
          <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">Add images</span>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <input
              type="file" multiple
              accept="image/png,image/jpeg,image/webp,image/gif"
              data-testid="admin-product-image-file"
              disabled={uploading}
              className="flex-1 bg-black border-2 border-white/20 text-white text-sm p-2 file:mr-3 file:bg-[#FF1B91] file:text-white file:border-0 file:px-4 file:py-2 file:text-[10px] file:uppercase file:tracking-[0.2em] file:font-bold file:cursor-pointer"
            />
            <button type="submit" disabled={uploading} data-testid="admin-product-image-upload"
              className="inline-flex items-center justify-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] disabled:opacity-50">
              <UploadSimple size={14} weight="bold" />{uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
          <p className="text-white/40 text-[11px]">PNG · JPG · WEBP · GIF · max 12MB each. Uploaded images automatically join the same gallery you can reorder below.</p>
        </form>
      )}

      {err && <p data-testid="admin-product-image-error" className="text-red-400 text-sm border border-red-500/40 bg-red-500/10 p-3">{err}</p>}
      {msg && <p data-testid="admin-product-image-msg" className="text-emerald-400 text-sm border border-emerald-500/40 bg-emerald-500/10 p-3">{msg}</p>}

      {productId && !loading && (
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
              Gallery ({galleryOrder.length}) · drag to reorder
            </p>
            <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-bold">
              <Star size={11} weight="fill" className="inline text-[#FF1B91] mr-1" /> Primary ·
              <EyeSlash size={11} weight="bold" className="inline ml-2 mr-1" /> Hidden
            </p>
          </div>
          {galleryOrder.length === 0 ? (
            <div className="border-2 border-dashed border-white/15 p-8 text-center text-white/40 text-sm">
              <ImageSquare size={32} weight="thin" className="mx-auto mb-2" />
              No images. Upload above.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {galleryOrder.map((url, i) => {
                const upload = customImages.find((c) => `/api/product-images/${c.id}` === url);
                const isHidden = hiddenSet.has(url);
                const isPrimary = primaryUrl === url || (i === 0 && !primaryUrl);
                const testKey = isUpload(url) ? url.split("/").pop() : `pf-${i}`;
                return (
                  <div
                    key={url}
                    draggable
                    onDragStart={(e) => onDragStart(e, url)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, url)}
                    data-testid={`admin-gallery-item-${testKey}`}
                    className={`relative border-2 bg-white aspect-square overflow-hidden group cursor-move ${dragUrl === url ? "border-[#FF1B91] opacity-40" : isPrimary ? "border-[#FF1B91]" : "border-white/10"} ${isHidden ? "opacity-40" : ""}`}
                  >
                    <img src={resolveImgSrc(url)} alt={upload?.original_filename || (isUpload(url) ? "Uploaded image" : "Printful mockup")} className="w-full h-full object-contain" draggable={false} />
                    <div className="absolute top-1 left-1 flex flex-col gap-1">
                      <span className="bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5">#{i + 1}</span>
                      {isPrimary && !isHidden && (
                        <span className="bg-[#FF1B91] text-white text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5">Primary</span>
                      )}
                      {isHidden && (
                        <span className="bg-red-500 text-white text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5">Hidden</span>
                      )}
                      {!isUpload(url) && (
                        <span className="bg-black/70 text-white/80 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5">Printful</span>
                      )}
                    </div>

                    <div className="absolute inset-x-0 bottom-0 flex flex-wrap justify-center gap-1 p-1 bg-gradient-to-t from-black/90 via-black/70 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button type="button" onClick={() => move(url, -1)} disabled={i === 0} aria-label="Move earlier" data-testid={`admin-gallery-up-${testKey}`}
                        className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/30 border border-white/30 text-white disabled:opacity-25">
                        <ArrowUp size={12} weight="bold" />
                      </button>
                      <button type="button" onClick={() => move(url, 1)} disabled={i === galleryOrder.length - 1} aria-label="Move later" data-testid={`admin-gallery-down-${testKey}`}
                        className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/30 border border-white/30 text-white disabled:opacity-25">
                        <ArrowDown size={12} weight="bold" />
                      </button>
                      <button type="button" onClick={() => setPrimary(url)} aria-label={isPrimary ? "Already primary" : "Set as primary"}
                        data-testid={`admin-gallery-primary-${testKey}`}
                        className={`w-8 h-8 flex items-center justify-center border border-white/30 text-white ${isPrimary ? "bg-[#FF1B91]" : "bg-white/15 hover:bg-[#FF1B91]"}`}>
                        <Star size={12} weight={isPrimary ? "fill" : "bold"} />
                      </button>
                      <button type="button" onClick={() => toggleHidden(url)} aria-label={isHidden ? "Show in shop" : "Hide from shop"}
                        data-testid={`admin-gallery-hide-${testKey}`}
                        className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-red-500 border border-white/30 text-white">
                        {isHidden ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}
                      </button>
                      {isUpload(url) && (
                        <button type="button" onClick={() => onDelete(url)} aria-label="Delete uploaded image"
                          data-testid={`admin-gallery-delete-${testKey}`}
                          className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-red-500 border border-white/30 text-white">
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                    <span className="absolute top-1 right-1 opacity-40 text-white bg-black/60 rounded-sm p-0.5"><DotsSixVertical size={12} /></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>
    </CollapsibleSection>
  );
}
